import { NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  LayoutDashboard,
  ShieldCheck,
  Users,
  Database,
  Fuel,
  FileX2,
  ScanLine,
  ChevronDown,
  LogOut,
  BarChart3,
  Archive,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_2999b2bb-a766-4d2f-9d69-24fdb875566a/artifacts/5zlw12hm_fa-logo.png";

export const MASTER_DATA_ITEMS = [
  { label: "Country", path: "/master-data/countries" },
  { label: "State", path: "/master-data/states" },
  { label: "District", path: "/master-data/districts" },
  { label: "City", path: "/master-data/cities" },
  { label: "Taluk", path: "/master-data/taluks" },
  { label: "Area", path: "/master-data/areas" },
  { label: "Street", path: "/master-data/streets" },
  { label: "Pincode", path: "/master-data/pincodes" },
  { label: "Company", path: "/master-data/companies" },
  { label: "Number of Wheels", path: "/master-data/wheel-counts" },
  { label: "Vehicle Brands", path: "/master-data/vehicle-brands" },
  { label: "Engine Stroke Types", path: "/master-data/engine-stroke-types" },
  { label: "Category of Cylinder", path: "/master-data/cylinder-categories" },
  { label: "Fuel Access Prefix", path: "/master-data/fuel-access-prefixes" },
  { label: "Cylinder Capacity", path: "/master-data/cylinder-capacities" },
  { label: "Test Years", path: "/master-data/test-years" },
  { label: "Retail Outlet", path: "/master-data/retail-outlets" },
  { label: "Testing Agencies", path: "/master-data/testing-agencies" },
  { label: "Scan Messages", path: "/master-data/scan-messages" },
];

const TOP_ITEMS = [
  { label: "Dashboard", path: "/dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { label: "Role & Permission", path: "/roles-permissions", icon: ShieldCheck, testid: "nav-roles-permissions", superadminOnly: true },
  { label: "Member", path: "/members", icon: Users, testid: "nav-members" },
];

const BOTTOM_ITEMS = [
  { label: "Fuel Access", path: "/fuel-access", icon: Fuel, testid: "nav-fuel-access" },
  { label: "Vehicle Registration", path: "/vehicle-registration", icon: FileX2, testid: "nav-blank-fuel-access" },
  { label: "Scan History", path: "/scan-history", icon: ScanLine, testid: "nav-scan-history" },
  { label: "Old Vehicles", path: "/old-vehicles", icon: Archive, testid: "nav-old-vehicles" },
];

export const REPORT_ITEMS = [
  { label: "Master Vehicle Registration Report", path: "/reports/vehicle-registration" },
  { label: "Master Scan Report", path: "/reports/scan" },
  { label: "Vehicles by User", path: "/reports/vehicles-by-user" },
  { label: "Vehicles by Bunk", path: "/reports/vehicles-by-bunk" },
  { label: "Vehicles by Company", path: "/reports/vehicles-by-company" },
  { label: "Vehicles by City", path: "/reports/vehicles-by-city" },
  { label: "Vehicles by District", path: "/reports/vehicles-by-district" },
  { label: "Vehicles by State", path: "/reports/vehicles-by-state" },
  { label: "Vehicles by Country", path: "/reports/vehicles-by-country" },
  { label: "Scan by User", path: "/reports/scan-by-user" },
  { label: "Scan by Bunk", path: "/reports/scan-by-bunk" },
  { label: "Scan by Company", path: "/reports/scan-by-company" },
  { label: "Scan by City", path: "/reports/scan-by-city" },
  { label: "Scan by District", path: "/reports/scan-by-district" },
  { label: "Scan by State", path: "/reports/scan-by-state" },
  { label: "Scan by Country", path: "/reports/scan-by-country" },
];

function navClass({ isActive }) {
  return [
    "group flex items-center gap-3 px-3 h-10 rounded-md text-sm transition-colors",
    isActive
      ? "bg-[#F50E02] text-white"
      : "text-[#0A0A0A] hover:bg-[#F50E02]/10 hover:text-[#F50E02]",
  ].join(" ");
}

function subNavClass({ isActive }) {
  return [
    "flex items-center px-3 h-9 rounded-md text-sm transition-colors",
    isActive
      ? "bg-[#F50E02]/10 text-[#F50E02] font-medium"
      : "text-[#525252] hover:text-[#F50E02] hover:bg-[#F50E02]/5",
  ].join(" ");
}

export default function Sidebar({ isOpen = false, onClose = () => {} }) {
  const location = useLocation();
  const { user, logout, canView } = useAuth();

  const isSuperadmin = user?.role?.toLowerCase() === "superadmin";

  const visibleMasterData = MASTER_DATA_ITEMS.filter((i) => canView(i.path));
  const visibleReports = REPORT_ITEMS.filter((i) => canView(i.path));
  const visibleTop = TOP_ITEMS.filter((i) => {
    if (i.superadminOnly && !isSuperadmin) return false;
    return canView(i.path);
  });
  const visibleBottom = BOTTOM_ITEMS.filter((i) => canView(i.path));

  const masterDataActive = location.pathname.startsWith("/master-data");
  const [masterOpen, setMasterOpen] = useState(masterDataActive);

  const reportsActive = location.pathname.startsWith("/reports");
  const [reportsOpen, setReportsOpen] = useState(reportsActive);

  useEffect(() => {
    if (masterDataActive) setMasterOpen(true);
  }, [masterDataActive]);

  useEffect(() => {
    if (reportsActive) setReportsOpen(true);
  }, [reportsActive]);

  return (
    <aside
      className={[
        "fixed top-0 left-0 z-30 w-[264px] h-full bg-white border-r border-black/5 flex flex-col",
        "transform transition-transform duration-300 ease-in-out",
        isOpen ? "translate-x-0" : "-translate-x-full",
        "md:static md:translate-x-0 md:h-screen md:z-auto md:shrink-0 md:sticky md:top-0",
      ].join(" ")}
      data-testid="app-sidebar"
    >
      {/* Brand */}
      <div className="px-5 py-5 flex items-center gap-3 border-b border-black/5">
        <div className="bg-black rounded-full p-1">
          <img src={LOGO_URL} alt="Sree LPG Fuel Access" className="h-8 w-8 object-contain" />
        </div>
        <div className="leading-tight">
          <p className="font-display text-sm font-semibold text-[#0A0A0A]">
            Sree LPG <span className="text-[#F50E02]">Fuel Access</span>
          </p>
          <p className="text-[10px] uppercase tracking-wider text-[#525252] mt-0.5">
            Superadmin Panel
          </p>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1" data-testid="sidebar-nav">
        {visibleTop.map(({ label, path, icon: Icon, testid }) => (
          <NavLink key={path} to={path} className={navClass} data-testid={testid} onClick={onClose}>
            <Icon className="h-4 w-4 shrink-0" />
            <span>{label}</span>
          </NavLink>
        ))}

        {/* Master Data group */}
        {visibleMasterData.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setMasterOpen((v) => !v)}
            className={[
              "w-full group flex items-center gap-3 px-3 h-10 rounded-md text-sm transition-colors",
              masterDataActive
                ? "bg-[#F50E02] text-white"
                : "text-[#0A0A0A] hover:bg-[#F50E02]/10 hover:text-[#F50E02]",
            ].join(" ")}
            data-testid="nav-master-data"
            aria-expanded={masterOpen}
          >
            <Database className="h-4 w-4 shrink-0" />
            <span className="flex-1 text-left">Master Data</span>
            <ChevronDown
              className={`h-4 w-4 transition-transform ${masterOpen ? "rotate-180" : ""}`}
            />
          </button>
          {masterOpen && (
            <div
              className="mt-1 ml-3 pl-3 border-l border-black/5 space-y-0.5"
              data-testid="master-data-submenu"
            >
              {visibleMasterData.map(({ label, path }) => (
                <NavLink
                  key={path}
                  to={path}
                  className={subNavClass}
                  data-testid={`nav-${path.split("/").pop()}`}
                  onClick={onClose}
                >
                  {label}
                </NavLink>
              ))}
            </div>
          )}
        </div>
        )}

        {visibleBottom.map(({ label, path, icon: Icon, testid }) => (
          <NavLink key={path} to={path} className={navClass} data-testid={testid} onClick={onClose}>
            <Icon className="h-4 w-4 shrink-0" />
            <span>{label}</span>
          </NavLink>
        ))}

        {/* Reports group */}
        {visibleReports.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setReportsOpen((v) => !v)}
            className={[
              "w-full group flex items-center gap-3 px-3 h-10 rounded-md text-sm transition-colors",
              reportsActive
                ? "bg-[#F50E02] text-white"
                : "text-[#0A0A0A] hover:bg-[#F50E02]/10 hover:text-[#F50E02]",
            ].join(" ")}
            data-testid="nav-reports"
            aria-expanded={reportsOpen}
          >
            <BarChart3 className="h-4 w-4 shrink-0" />
            <span className="flex-1 text-left">Reports</span>
            <ChevronDown
              className={`h-4 w-4 transition-transform ${reportsOpen ? "rotate-180" : ""}`}
            />
          </button>
          {reportsOpen && (
            <div
              className="mt-1 ml-3 pl-3 border-l border-black/5 space-y-0.5"
              data-testid="reports-submenu"
            >
              {visibleReports.map(({ label, path }) => (
                <NavLink
                  key={path}
                  to={path}
                  className={subNavClass}
                  data-testid={`nav-${path.split("/").pop()}`}
                  onClick={onClose}
                >
                  {label}
                </NavLink>
              ))}
            </div>
          )}
        </div>
        )}
      </nav>

      {/* User footer */}
      <div className="border-t border-black/5 p-3">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-8 h-8 rounded-full bg-[#F50E02]/10 flex items-center justify-center">
            <span className="text-xs font-semibold text-[#F50E02]">
              {(user?.name || user?.email || "A").slice(0, 1).toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p
              className="text-sm font-medium text-[#0A0A0A] truncate"
              data-testid="sidebar-user-email"
            >
              {user?.email}
            </p>
            <p className="text-[10px] uppercase tracking-wider text-[#525252]">
              {user?.role}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={logout}
          className="mt-2 w-full flex items-center justify-center gap-2 h-9 rounded-md text-sm font-medium text-[#525252] border border-black/10 hover:bg-[#F50E02] hover:text-white hover:border-[#F50E02] transition-colors"
          data-testid="sidebar-logout-button"
        >
          <LogOut className="h-4 w-4" />
          Logout
        </button>
      </div>
    </aside>
  );
}
